import { DeleteOutlined, StopOutlined } from '@ant-design/icons';
import { useCallback } from 'react';
import styled from 'styled-components';

import { Divider, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Checkbox from '@/components/Checkbox/Checkbox';
import Transition from '@/components/Common/Transition';
import Typo from '@/components/Typography/Typo';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import { radius } from '@/styles/sizes';

import { scrapingService } from '../../api';
import { SCRAPING_JOB_TYPE } from '../../types';

type Props = {
  isAllSelected: boolean;
  onCheckAll: () => void;
  selectedList: string[];
  scrapingJobType: SCRAPING_JOB_TYPE;
  onClear: () => void;
  refetchCallback: () => void;
};

const FloatActionMenu = (props: Props) => {
  const setPopup = usePopup();
  const addToast = useToast();

  const onCancelScrapingJobHandler = useCallback(() => {
    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Cancel job',
      message: 'Are you sure you want to cancel the selected jobs?',
      onConfirm: async () => {
        await scrapingService.cancelScrapingJob(props.selectedList);
        addToast('Scraping jobs have been canceled', 'success');
        props.onClear();
        props.refetchCallback();
      },
    });
  }, [addToast, props, setPopup]);

  const onDeleteScrapingJobHandler = useCallback(() => {
    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Delete schedule',
      message: 'Are you sure you want to delete the selected schedules?',
      onConfirm: async () => {
        await scrapingService.deleteScrapingJob(props.selectedList);
        addToast('Schedules have been deleted', 'success');
        props.onClear();
        props.refetchCallback();
      },
    });
  }, [addToast, props, setPopup]);

  return (
    <Transition status={props.selectedList.length > 0}>
      <Style>
        <Flex g={12}>
          <Flex g={12} a="center">
            <Checkbox
              checked={props.isAllSelected}
              halfCheck={!props.isAllSelected}
              onChange={() => {
                if (props.isAllSelected) {
                  props.onClear();
                } else {
                  props.onCheckAll();
                }
              }}
            />
            <Typo type="label2" className="text-gray-600">
              {props.selectedList.length} selected
            </Typo>
          </Flex>
          <Divider direction="v" />
          {props.scrapingJobType === SCRAPING_JOB_TYPE.ONETIME && (
            <Button
              leftIcon={<StopOutlined />}
              variant="ghost"
              sizeType="s"
              onClick={onCancelScrapingJobHandler}
            >
              Cancel job
            </Button>
          )}
          {props.scrapingJobType === SCRAPING_JOB_TYPE.SCHEDULED && (
            <Button
              leftIcon={<DeleteOutlined />}
              variant="ghost"
              sizeType="s"
              onClick={onDeleteScrapingJobHandler}
            >
              Delete schedule
            </Button>
          )}
        </Flex>
      </Style>
    </Transition>
  );
};

const Style = styled(VFlex)`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  align-items: center;
  bottom: 48px;
  gap: 12px;
  padding: 12px;
  border-radius: ${radius.card}px;
  background-color: ${(props) => props.theme.colors.gray100};
  border: 1px solid ${(props) => props.theme.colors.primary100};
  & > * {
    flex-shrink: 0;
  }
`;

export default FloatActionMenu;
